/** @typedef {[number, number]} Point */
/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */

import { findPointNear, getPointById } from './floorPlanDesignerState.js';
import { projectPointOntoSegment } from './floorPlanDesignerGeometry.js';

const AXIS_EPS_PX = 1;

export const SNAP_ANGLES_DEG = [0, 45, 90, 135, 180, 225, 270, 315];
export const ANGLE_SNAP_TOLERANCE_DEG = 3;
export const ORTHO_SNAP_PX = 8;
export const OTRACK_SNAP_PX = 8;
export const ORTHO_ANGLE_SNAP_DEG = 5;
export const angleSnapEnabled = true;

/**
 * @typedef {{ point: Point; snapped: boolean; snappedAngleDeg: number | null }} AngleSnapResult
 */

/**
 * @param {number} angle
 * @returns {number}
 */
export function normalizeAngleDeg(angle) {
  return ((angle % 360) + 360) % 360;
}

/**
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
export function shortestAngleDiffDeg(a, b) {
  const diff = Math.abs(normalizeAngleDeg(a) - normalizeAngleDeg(b));
  return Math.min(diff, 360 - diff);
}

/**
 * @param {Point} anchor
 * @param {Point} current
 * @param {{ toleranceDeg?: number; snapAnglesDeg?: number[] }} [options]
 * @returns {AngleSnapResult}
 */
export function snapPointToAngleIfClose(anchor, current, options = {}) {
  const toleranceDeg = options.toleranceDeg ?? ANGLE_SNAP_TOLERANCE_DEG;
  const snapAnglesDeg = options.snapAnglesDeg ?? SNAP_ANGLES_DEG;

  const dx = current[0] - anchor[0];
  const dy = current[1] - anchor[1];
  const length = Math.hypot(dx, dy);

  if (length <= 0.0001) {
    return { point: current, snapped: false, snappedAngleDeg: null };
  }

  const angleRad = Math.atan2(dy, dx);
  const angleDeg = normalizeAngleDeg((angleRad * 180) / Math.PI);

  let bestAngle = null;
  let bestDiff = Infinity;

  for (const targetAngle of snapAnglesDeg) {
    const diff = shortestAngleDiffDeg(angleDeg, targetAngle);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestAngle = targetAngle;
    }
  }

  if (bestAngle === null || bestDiff > toleranceDeg) {
    return { point: current, snapped: false, snappedAngleDeg: null };
  }

  const snappedRad = (bestAngle * Math.PI) / 180;
  return {
    point: [
      anchor[0] + Math.cos(snappedRad) * length,
      anchor[1] + Math.sin(snappedRad) * length,
    ],
    snapped: true,
    snappedAngleDeg: bestAngle,
  };
}

/**
 * @param {number} screenPx
 * @param {number} displayScale
 * @returns {number}
 */
export function screenToleranceToImagePx(screenPx, displayScale) {
  const scale = displayScale > 0 ? displayScale : 1;
  return screenPx / scale;
}

/**
 * @param {{ x: number; y: number }} point
 * @returns {Point}
 */
function pointObjectToTuple(point) {
  return [point.x, point.y];
}

/**
 * @param {Point} point
 * @returns {{ x: number; y: number }}
 */
function tupleToPointObject(point) {
  return { x: point[0], y: point[1] };
}

/**
 * @param {DesignerState} state
 * @param {string | null | undefined} pointId
 * @param {string[]} connectedWallIds
 * @param {string[]} excludeWallIds
 * @returns {Array<{ wallId: string; fixedPointId: string; fixed: Point }>}
 */
function connectedWallAnchors(state, pointId, connectedWallIds = [], excludeWallIds = []) {
  const wallIdSet = new Set(connectedWallIds);
  const excludedWalls = new Set(excludeWallIds);
  const anchors = [];
  if (!pointId && wallIdSet.size === 0) return anchors;

  for (const wall of state.walls) {
    if (excludedWalls.has(wall.id)) continue;
    const isConnected =
      (pointId && (wall.startPointId === pointId || wall.endPointId === pointId)) ||
      wallIdSet.has(wall.id);
    if (!isConnected) continue;

    let fixedPointId = null;
    if (pointId && wall.startPointId === pointId) fixedPointId = wall.endPointId;
    else if (pointId && wall.endPointId === pointId) fixedPointId = wall.startPointId;
    else continue;

    const fixed = getPointById(state, fixedPointId);
    if (!fixed) continue;
    anchors.push({
      wallId: wall.id,
      fixedPointId,
      fixed: pointObjectToTuple(fixed),
    });
  }
  return anchors;
}

/**
 * @param {Point} active
 * @param {{ x: number; y: number }} source
 * @param {'horizontal' | 'vertical'} type
 * @param {string} sourcePointId
 * @param {'connected-wall-ortho' | 'otrack-node-align'} reason
 */
function buildSnapGuide(active, source, type, sourcePointId, reason) {
  if (type === 'horizontal') {
    return {
      type,
      from: { x: source.x, y: source.y },
      to: { x: active[0], y: source.y },
      sourcePointId,
      reason,
    };
  }
  return {
    type,
    from: { x: source.x, y: source.y },
    to: { x: source.x, y: active[1] },
    sourcePointId,
    reason,
  };
}

/**
 * @param {Array<ReturnType<typeof buildSnapGuide>>} guides
 * @param {Point} active
 */
function updateGuideEndpoints(guides, active) {
  return guides.map((guide) => {
    if (guide.type === 'horizontal') {
      return { ...guide, to: { x: active[0], y: guide.from.y } };
    }
    return { ...guide, to: { x: guide.from.x, y: active[1] } };
  });
}

/**
 * @param {Point} point
 * @param {number} gridMeters
 * @param {number} pixelsPerMeter
 * @returns {Point}
 */
export function snapPointToGridMeters(point, gridMeters, pixelsPerMeter) {
  const stepPx = gridMeters * pixelsPerMeter;
  if (stepPx <= 0) return point;
  return [
    Math.round(point[0] / stepPx) * stepPx,
    Math.round(point[1] / stepPx) * stepPx,
  ];
}

/**
 * Applies direct connected-wall H/V snapping, then oTrack node alignment, then grid
 * on any axis that was not already snapped by a higher-priority rule.
 *
 * @param {{
 *   activePoint: Point;
 *   state: DesignerState;
 *   pointId?: string | null;
 *   connectedWallIds?: string[];
 *   orthoAnchorPointIds?: string[];
 *   excludePointIds?: string[];
 *   excludeWallIds?: string[];
 *   snapToGrid?: boolean;
 *   gridMeters?: number;
 *   displayScale?: number;
 *   includeConnectedWallOrtho?: boolean;
 *   includeOTrack?: boolean;
 *   orthoSnapPx?: number;
 *   otrackSnapPx?: number;
 * }} input
 * @returns {{
 *   point: Point;
 *   guides: Array<{
 *     type: 'horizontal' | 'vertical';
 *     from: { x: number; y: number };
 *     to: { x: number; y: number };
 *     sourcePointId: string;
 *     reason: 'connected-wall-ortho' | 'otrack-node-align';
 *   }>;
 *   snapInfo: {
 *     snappedX: boolean;
 *     snappedY: boolean;
 *     reasonX: string | null;
 *     reasonY: string | null;
 *   };
 * }}
 */
export function resolveOrthogonalAndOTrackSnap(input) {
  const {
    activePoint,
    state,
    pointId = null,
    connectedWallIds = [],
    orthoAnchorPointIds = [],
    excludePointIds = [],
    excludeWallIds = [],
    snapToGrid = false,
    gridMeters = 0.1,
    displayScale = 1,
    includeConnectedWallOrtho = true,
    includeOTrack = true,
    orthoSnapPx = ORTHO_SNAP_PX,
    otrackSnapPx = OTRACK_SNAP_PX,
  } = input;

  const orthoTolerance = screenToleranceToImagePx(orthoSnapPx, displayScale);
  const otrackTolerance = screenToleranceToImagePx(otrackSnapPx, displayScale);
  const excludedPoints = new Set(excludePointIds);
  if (pointId) excludedPoints.add(pointId);

  /** @type {Point} */
  let next = [...activePoint];
  const guides = [];
  const snapInfo = {
    snappedX: false,
    snappedY: false,
    reasonX: /** @type {string | null} */ (null),
    reasonY: /** @type {string | null} */ (null),
  };

  if (includeConnectedWallOrtho) {
    const anchors = connectedWallAnchors(state, pointId, connectedWallIds, excludeWallIds);
    for (const anchorPointId of orthoAnchorPointIds) {
      const anchorPoint = getPointById(state, anchorPointId);
      if (!anchorPoint) continue;
      anchors.push({
        wallId: '',
        fixedPointId: anchorPointId,
        fixed: pointObjectToTuple(anchorPoint),
      });
    }
    let bestX = null;
    let bestY = null;
    for (const anchor of anchors) {
      const dx = Math.abs(activePoint[0] - anchor.fixed[0]);
      const dy = Math.abs(activePoint[1] - anchor.fixed[1]);
      if (dx <= orthoTolerance && (!bestX || dx < bestX.dist)) {
        bestX = { dist: dx, value: anchor.fixed[0], anchor };
      }
      if (dy <= orthoTolerance && (!bestY || dy < bestY.dist)) {
        bestY = { dist: dy, value: anchor.fixed[1], anchor };
      }
    }

    if (bestX) {
      next[0] = bestX.value;
      snapInfo.snappedX = true;
      snapInfo.reasonX = 'connected-wall-ortho';
      guides.push(buildSnapGuide(
        next,
        tupleToPointObject(bestX.anchor.fixed),
        'vertical',
        bestX.anchor.fixedPointId,
        'connected-wall-ortho',
      ));
    }
    if (bestY) {
      next[1] = bestY.value;
      snapInfo.snappedY = true;
      snapInfo.reasonY = 'connected-wall-ortho';
      guides.push(buildSnapGuide(
        next,
        tupleToPointObject(bestY.anchor.fixed),
        'horizontal',
        bestY.anchor.fixedPointId,
        'connected-wall-ortho',
      ));
    }
  }

  if (includeOTrack) {
    let bestX = null;
    let bestY = null;
    for (const other of state.points) {
      if (excludedPoints.has(other.id)) continue;
      const dx = Math.abs(activePoint[0] - other.x);
      const dy = Math.abs(activePoint[1] - other.y);
      if (!snapInfo.snappedX && dx <= otrackTolerance && (!bestX || dx < bestX.dist)) {
        bestX = { dist: dx, point: other };
      }
      if (!snapInfo.snappedY && dy <= otrackTolerance && (!bestY || dy < bestY.dist)) {
        bestY = { dist: dy, point: other };
      }
    }

    if (bestX) {
      next[0] = bestX.point.x;
      snapInfo.snappedX = true;
      snapInfo.reasonX = 'otrack-node-align';
      guides.push(buildSnapGuide(next, bestX.point, 'vertical', bestX.point.id, 'otrack-node-align'));
    }
    if (bestY) {
      next[1] = bestY.point.y;
      snapInfo.snappedY = true;
      snapInfo.reasonY = 'otrack-node-align';
      guides.push(buildSnapGuide(next, bestY.point, 'horizontal', bestY.point.id, 'otrack-node-align'));
    }
  }

  if (snapToGrid) {
    const gridPoint = snapPointToGridMeters(next, gridMeters, state.scale.pixelsPerMeter);
    if (!snapInfo.snappedX) next[0] = gridPoint[0];
    if (!snapInfo.snappedY) next[1] = gridPoint[1];
  }

  return {
    point: next,
    guides: updateGuideEndpoints(guides, next),
    snapInfo,
  };
}

/**
 * @param {{
 *   startPoint: Point;
 *   endPoint: Point;
 *   state: DesignerState;
 *   startPointId?: string | null;
 *   endPointId?: string | null;
 *   excludePointIds?: string[];
 *   excludeWallIds?: string[];
 *   displayScale?: number;
 *   includeConnectedWallOrtho?: boolean;
 *   orthoSnapPx?: number;
 *   otrackSnapPx?: number;
 * }} input
 * @returns {{
 *   delta: Point;
 *   guides: ReturnType<typeof resolveOrthogonalAndOTrackSnap>['guides'];
 * }}
 */
export function resolveWallTranslationOTrackSnap(input) {
  const {
    startPoint,
    endPoint,
    state,
    startPointId = null,
    endPointId = null,
    excludePointIds = [],
    excludeWallIds = [],
    displayScale = 1,
    includeConnectedWallOrtho = true,
    orthoSnapPx = ORTHO_SNAP_PX,
    otrackSnapPx = OTRACK_SNAP_PX,
  } = input;
  const orthoTolerance = screenToleranceToImagePx(orthoSnapPx, displayScale);
  const otrackTolerance = screenToleranceToImagePx(otrackSnapPx, displayScale);
  const excludedPoints = new Set(excludePointIds);
  const candidates = [
    { point: startPoint, pointId: startPointId, endpoint: 'start' },
    { point: endPoint, pointId: endPointId, endpoint: 'end' },
  ];
  let bestConnectedX = null;
  let bestConnectedY = null;

  if (includeConnectedWallOrtho) {
    for (const candidate of candidates) {
      if (!candidate.pointId) continue;
      const anchors = connectedWallAnchors(state, candidate.pointId, [], excludeWallIds);
      for (const anchor of anchors) {
        const dx = anchor.fixed[0] - candidate.point[0];
        const dy = anchor.fixed[1] - candidate.point[1];
        if (Math.abs(dx) <= orthoTolerance && (!bestConnectedX || Math.abs(dx) < Math.abs(bestConnectedX.delta))) {
          bestConnectedX = {
            delta: dx,
            source: tupleToPointObject(anchor.fixed),
            sourcePointId: anchor.fixedPointId,
            endpoint: candidate.endpoint,
          };
        }
        if (Math.abs(dy) <= orthoTolerance && (!bestConnectedY || Math.abs(dy) < Math.abs(bestConnectedY.delta))) {
          bestConnectedY = {
            delta: dy,
            source: tupleToPointObject(anchor.fixed),
            sourcePointId: anchor.fixedPointId,
            endpoint: candidate.endpoint,
          };
        }
      }
    }
  }

  const connectedDelta = /** @type {Point} */ ([
    bestConnectedX ? bestConnectedX.delta : 0,
    bestConnectedY ? bestConnectedY.delta : 0,
  ]);
  const connectedStart = /** @type {Point} */ ([startPoint[0] + connectedDelta[0], startPoint[1] + connectedDelta[1]]);
  const connectedEnd = /** @type {Point} */ ([endPoint[0] + connectedDelta[0], endPoint[1] + connectedDelta[1]]);
  const otrackCandidates = [
    { point: connectedStart, pointId: startPointId, endpoint: 'start' },
    { point: connectedEnd, pointId: endPointId, endpoint: 'end' },
  ];
  let bestX = null;
  let bestY = null;

  for (const candidate of otrackCandidates) {
    for (const other of state.points) {
      if (excludedPoints.has(other.id)) continue;
      const dx = other.x - candidate.point[0];
      const dy = other.y - candidate.point[1];
      if (!bestConnectedX && Math.abs(dx) <= otrackTolerance && (!bestX || Math.abs(dx) < Math.abs(bestX.delta))) {
        bestX = { delta: dx, source: other, endpoint: candidate.endpoint };
      }
      if (!bestConnectedY && Math.abs(dy) <= otrackTolerance && (!bestY || Math.abs(dy) < Math.abs(bestY.delta))) {
        bestY = { delta: dy, source: other, endpoint: candidate.endpoint };
      }
    }
  }

  const delta = /** @type {Point} */ ([
    connectedDelta[0] + (bestX ? bestX.delta : 0),
    connectedDelta[1] + (bestY ? bestY.delta : 0),
  ]);
  const snappedStart = [startPoint[0] + delta[0], startPoint[1] + delta[1]];
  const snappedEnd = [endPoint[0] + delta[0], endPoint[1] + delta[1]];
  const guides = [];
  if (bestConnectedX) {
    const active = bestConnectedX.endpoint === 'start' ? snappedStart : snappedEnd;
    guides.push(buildSnapGuide(active, bestConnectedX.source, 'vertical', bestConnectedX.sourcePointId, 'connected-wall-ortho'));
  }
  if (bestConnectedY) {
    const active = bestConnectedY.endpoint === 'start' ? snappedStart : snappedEnd;
    guides.push(buildSnapGuide(active, bestConnectedY.source, 'horizontal', bestConnectedY.sourcePointId, 'connected-wall-ortho'));
  }
  if (bestX) {
    const active = bestX.endpoint === 'start' ? snappedStart : snappedEnd;
    guides.push(buildSnapGuide(active, bestX.source, 'vertical', bestX.source.id, 'otrack-node-align'));
  }
  if (bestY) {
    const active = bestY.endpoint === 'start' ? snappedStart : snappedEnd;
    guides.push(buildSnapGuide(active, bestY.source, 'horizontal', bestY.source.id, 'otrack-node-align'));
  }

  return { delta, guides };
}

/**
 * @deprecated Use snapPointToAngleIfClose instead.
 * @param {Point} anchor
 * @param {Point} pointer
 * @returns {Point}
 */
export function snapToOrthogonal(anchor, pointer) {
  const dx = Math.abs(pointer[0] - anchor[0]);
  const dy = Math.abs(pointer[1] - anchor[1]);
  if (dx >= dy) return [pointer[0], anchor[1]];
  return [anchor[0], pointer[1]];
}

/**
 * @param {Point} a
 * @param {Point} b
 * @returns {'horizontal' | 'vertical' | 'angled'}
 */
export function classifySegmentAxis(a, b) {
  const dx = Math.abs(b[0] - a[0]);
  const dy = Math.abs(b[1] - a[1]);
  if (dy <= AXIS_EPS_PX) return 'horizontal';
  if (dx <= AXIS_EPS_PX) return 'vertical';
  return 'angled';
}

/**
 * @typedef {{ snapped: boolean; snappedAngleDeg: number | null; anchor: Point | null }} AngleSnapMeta
 */

/**
 * @param {Point} pointer
 * @param {DesignerState} state
 * @param {{
 *   snapToGrid?: boolean;
 *   snapToPoints?: boolean;
 *   snapToWalls?: boolean;
 *   snapTolerancePx?: number;
 *   gridMeters?: number;
 *   displayScale?: number;
 *   angleSnapEnabled?: boolean;
 *   angleSnapToleranceDeg?: number;
 *   disableSnap?: boolean;
 *   anchor?: Point | null;
 * }} options
 * @returns {{ point: Point; angleSnap: AngleSnapMeta | null }}
 */
export function snapPointer(pointer, state, options = {}) {
  const {
    snapToGrid = true,
    snapToPoints = true,
    snapToWalls = false,
    snapTolerancePx = 12,
    gridMeters = 0.1,
    displayScale = 1,
    angleSnapEnabled: angleSnapOn = angleSnapEnabled,
    angleSnapToleranceDeg = ANGLE_SNAP_TOLERANCE_DEG,
    disableSnap = false,
    anchor = null,
  } = options;

  if (disableSnap) {
    return { point: pointer, angleSnap: null };
  }

  const tolerance = screenToleranceToImagePx(snapTolerancePx, displayScale);
  const ppm = state.scale.pixelsPerMeter;

  if (snapToPoints) {
    const near = findPointNear(state, pointer, tolerance);
    if (near) return { point: [near.x, near.y], angleSnap: null };
  }

  let next = [...pointer];

  if (snapToGrid) {
    next = snapPointToGridMeters(next, gridMeters, ppm);
  }

  /** @type {AngleSnapMeta | null} */
  let angleSnap = null;

  if (anchor && angleSnapOn) {
    const angleResult = snapPointToAngleIfClose(anchor, next, {
      toleranceDeg: angleSnapToleranceDeg,
    });
    next = angleResult.point;
    if (angleResult.snapped) {
      angleSnap = {
        snapped: true,
        snappedAngleDeg: angleResult.snappedAngleDeg,
        anchor: [...anchor],
      };
    }
  }

  if (snapToWalls && state.walls.length) {
    let best = null;
    let bestDist = Infinity;
    for (const wall of state.walls) {
      const startPt = state.points.find((p) => p.id === wall.startPointId);
      const endPt = state.points.find((p) => p.id === wall.endPointId);
      if (!startPt || !endPt) continue;
      const { dist, projected } = projectPointOntoSegment(
        [startPt.x, startPt.y],
        [endPt.x, endPt.y],
        next,
      );
      if (dist < bestDist) {
        bestDist = dist;
        best = projected;
      }
    }
    if (best && bestDist <= tolerance) {
      return { point: best, angleSnap: null };
    }
  }

  return { point: next, angleSnap };
}

/**
 * Primary anchor for angle snapping while dragging a node (longest incident wall).
 * @param {string} pointId
 * @param {DesignerState} state
 * @returns {{ anchor: Point } | null}
 */
export function getDragAngleSnapAnchor(pointId, state) {
  const cornerPt = getPointById(state, pointId);
  if (!cornerPt) return null;

  /** @type {Array<{ anchor: Point; length: number }>} */
  const constraints = [];

  for (const wall of state.walls) {
    let fixed = null;
    if (wall.startPointId === pointId) {
      const endPt = getPointById(state, wall.endPointId);
      if (!endPt) continue;
      fixed = [endPt.x, endPt.y];
    } else if (wall.endPointId === pointId) {
      const startPt = getPointById(state, wall.startPointId);
      if (!startPt) continue;
      fixed = [startPt.x, startPt.y];
    } else {
      continue;
    }
    constraints.push({
      anchor: fixed,
      length: Math.hypot(fixed[0] - cornerPt.x, fixed[1] - cornerPt.y),
    });
  }

  if (constraints.length === 0) return null;

  const primary = constraints.reduce(
    (best, current) => (current.length > best.length ? current : best),
    constraints[0],
  );

  return { anchor: primary.anchor };
}

/**
 * @param {Point} pointer
 * @param {string} pointId
 * @param {DesignerState} state
 * @param {{ angleSnapToleranceDeg?: number }} options
 * @returns {{ point: Point; anchor: Point | null; snapped: boolean; snappedAngleDeg: number | null }}
 */
export function constrainCornerByIncidentWalls(pointer, pointId, state, options = {}) {
  const anchorInfo = getDragAngleSnapAnchor(pointId, state);
  if (!anchorInfo) {
    return { point: pointer, anchor: null, snapped: false, snappedAngleDeg: null };
  }

  const angleResult = snapPointToAngleIfClose(anchorInfo.anchor, pointer, {
    toleranceDeg: options.angleSnapToleranceDeg ?? ANGLE_SNAP_TOLERANCE_DEG,
  });

  return {
    point: angleResult.point,
    anchor: anchorInfo.anchor,
    snapped: angleResult.snapped,
    snappedAngleDeg: angleResult.snappedAngleDeg,
  };
}

function angleBetweenVectorsDeg(a, b) {
  const lenA = Math.hypot(a[0], a[1]);
  const lenB = Math.hypot(b[0], b[1]);
  if (lenA < 1e-6 || lenB < 1e-6) return null;
  const dot = (a[0] * b[0] + a[1] * b[1]) / (lenA * lenB);
  return (Math.acos(Math.max(-1, Math.min(1, dot))) * 180) / Math.PI;
}

function projectPointToDiameterCircle(point, a, b) {
  const center = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const radius = Math.hypot(b[0] - a[0], b[1] - a[1]) / 2;
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];
  const len = Math.hypot(dx, dy);
  if (radius < 1e-6 || len < 1e-6) return null;
  return [center[0] + (dx / len) * radius, center[1] + (dy / len) * radius];
}

function buildPerpendicularGuide(corner, legAEnd, legBEnd) {
  return {
    type: 'perpendicular',
    corner,
    legAEnd,
    legBEnd,
  };
}

export function snapToPerpendicularIncidentWalls(pointer, pointId, state, options = {}) {
  const {
    tolerancePx = ORTHO_SNAP_PX,
    angleToleranceDeg = ORTHO_ANGLE_SNAP_DEG,
  } = options;
  const anchors = connectedWallAnchors(state, pointId);
  if (anchors.length < 2) {
    return { point: pointer, snapped: false, angleSnap: null, perpendicularGuide: null };
  }

  let best = null;
  for (let i = 0; i < anchors.length; i += 1) {
    for (let j = i + 1; j < anchors.length; j += 1) {
      const a = anchors[i].fixed;
      const b = anchors[j].fixed;
      const angle = angleBetweenVectorsDeg(
        [a[0] - pointer[0], a[1] - pointer[1]],
        [b[0] - pointer[0], b[1] - pointer[1]],
      );
      if (angle == null) continue;
      const angleDiff = Math.abs(angle - 90);
      if (angleDiff > angleToleranceDeg) continue;

      const candidate = projectPointToDiameterCircle(pointer, a, b);
      if (!candidate) continue;
      const correction = Math.hypot(candidate[0] - pointer[0], candidate[1] - pointer[1]);
      if (correction > tolerancePx) continue;
      if (!best || correction < best.correction) {
        best = { candidate, correction, anchor: a, otherAnchor: b };
      }
    }
  }

  if (!best) {
    return { point: pointer, snapped: false, angleSnap: null, perpendicularGuide: null };
  }

  const angleDeg = normalizeAngleDeg(
    (Math.atan2(best.candidate[1] - best.anchor[1], best.candidate[0] - best.anchor[0]) * 180) /
      Math.PI,
  );
  return {
    point: best.candidate,
    snapped: true,
    perpendicularGuide: buildPerpendicularGuide(best.candidate, best.anchor, best.otherAnchor),
    angleSnap: {
      snapped: true,
      snappedAngleDeg: angleDeg,
      anchor: best.anchor,
    },
  };
}

export function snapPointToPerpendicularFromAnchor(pointer, anchorPointId, state, options = {}) {
  const {
    tolerancePx = ORTHO_SNAP_PX,
    angleToleranceDeg = ORTHO_ANGLE_SNAP_DEG,
    excludeWallIds = [],
  } = options;
  const anchorPoint = getPointById(state, anchorPointId);
  if (!anchorPoint) {
    return { point: pointer, snapped: false, angleSnap: null, perpendicularGuide: null };
  }

  const anchor = [anchorPoint.x, anchorPoint.y];
  const incidentAnchors = connectedWallAnchors(state, anchorPointId, [], excludeWallIds);
  let best = null;

  for (const incident of incidentAnchors) {
    const existingVector = [incident.fixed[0] - anchor[0], incident.fixed[1] - anchor[1]];
    const drawVector = [pointer[0] - anchor[0], pointer[1] - anchor[1]];
    const angle = angleBetweenVectorsDeg(existingVector, drawVector);
    if (angle == null) continue;
    const angleDiff = Math.abs(angle - 90);
    if (angleDiff > angleToleranceDeg) continue;

    const length = Math.hypot(drawVector[0], drawVector[1]);
    if (length < 1e-6) continue;
    const existingLen = Math.hypot(existingVector[0], existingVector[1]);
    if (existingLen < 1e-6) continue;
    const perpA = [-existingVector[1] / existingLen, existingVector[0] / existingLen];
    const perpB = [existingVector[1] / existingLen, -existingVector[0] / existingLen];
    const candidateA = [anchor[0] + perpA[0] * length, anchor[1] + perpA[1] * length];
    const candidateB = [anchor[0] + perpB[0] * length, anchor[1] + perpB[1] * length];
    const distA = Math.hypot(candidateA[0] - pointer[0], candidateA[1] - pointer[1]);
    const distB = Math.hypot(candidateB[0] - pointer[0], candidateB[1] - pointer[1]);
    const candidate = distA <= distB ? candidateA : candidateB;
    const correction = Math.min(distA, distB);
    if (correction > tolerancePx) continue;
    if (!best || correction < best.correction) {
      best = { candidate, correction, incident };
    }
  }

  if (!best) {
    return { point: pointer, snapped: false, angleSnap: null, perpendicularGuide: null };
  }

  const angleDeg = normalizeAngleDeg(
    (Math.atan2(best.candidate[1] - anchor[1], best.candidate[0] - anchor[0]) * 180) /
      Math.PI,
  );
  return {
    point: best.candidate,
    snapped: true,
    perpendicularGuide: buildPerpendicularGuide(anchor, best.incident.fixed, best.candidate),
    angleSnap: {
      snapped: true,
      snappedAngleDeg: angleDeg,
      anchor,
    },
  };
}

export function resolveWallTranslationPerpendicularSnap(input) {
  const {
    startPoint,
    endPoint,
    state,
    startPointId,
    endPointId,
    excludeWallIds = [],
    displayScale = 1,
    snapTolerancePx = ORTHO_SNAP_PX,
    angleToleranceDeg = ORTHO_ANGLE_SNAP_DEG,
  } = input;
  const tolerance = screenToleranceToImagePx(snapTolerancePx, displayScale);
  const wallVector = [endPoint[0] - startPoint[0], endPoint[1] - startPoint[1]];
  const wallLen = Math.hypot(wallVector[0], wallVector[1]);
  if (wallLen < 1e-6) return { delta: [0, 0], perpendicularGuide: null };

  const checks = [
    { point: startPoint, pointId: startPointId, otherPoint: endPoint },
    { point: endPoint, pointId: endPointId, otherPoint: startPoint },
  ];
  let best = null;

  for (const check of checks) {
    const anchors = connectedWallAnchors(state, check.pointId, [], excludeWallIds);
    for (const anchor of anchors) {
      const v = [
        check.otherPoint[0] - check.point[0],
        check.otherPoint[1] - check.point[1],
      ];
      const vLenSq = v[0] * v[0] + v[1] * v[1];
      if (vLenSq < 1e-6) continue;
      const currentDot = (anchor.fixed[0] - check.point[0]) * v[0] +
        (anchor.fixed[1] - check.point[1]) * v[1];
      const deltaScale = currentDot / vLenSq;
      const delta = [v[0] * deltaScale, v[1] * deltaScale];
      const correction = Math.hypot(delta[0], delta[1]);
      if (correction > tolerance) continue;
      const snappedPoint = [check.point[0] + delta[0], check.point[1] + delta[1]];
      const snappedOther = [check.otherPoint[0] + delta[0], check.otherPoint[1] + delta[1]];
      const snappedAngle = angleBetweenVectorsDeg(
        [anchor.fixed[0] - snappedPoint[0], anchor.fixed[1] - snappedPoint[1]],
        [snappedOther[0] - snappedPoint[0], snappedOther[1] - snappedPoint[1]],
      );
      if (snappedAngle == null || Math.abs(snappedAngle - 90) > angleToleranceDeg) continue;
      if (!best || correction < best.correction) {
        best = { delta, correction, check, anchor };
      }
    }
  }

  if (!best) return { delta: [0, 0], perpendicularGuide: null };
  return {
    delta: best.delta,
    perpendicularGuide: buildPerpendicularGuide(
      [best.check.point[0] + best.delta[0], best.check.point[1] + best.delta[1]],
      best.anchor.fixed,
      [best.check.otherPoint[0] + best.delta[0], best.check.otherPoint[1] + best.delta[1]],
    ),
  };
}

function getWallSnapCandidate(point, state, tolerance, excludeWallIds = []) {
  const exclude = new Set(excludeWallIds);
  let best = null;
  let bestDist = Infinity;

  for (const wall of state.walls) {
    if (exclude.has(wall.id)) continue;
    const startPt = state.points.find((p) => p.id === wall.startPointId);
    const endPt = state.points.find((p) => p.id === wall.endPointId);
    if (!startPt || !endPt) continue;

    const start = [startPt.x, startPt.y];
    const end = [endPt.x, endPt.y];
    const { dist, projected, t } = projectPointOntoSegment(start, end, point);
    if (dist > tolerance || dist >= bestDist) continue;
    if (t <= 0 || t >= 1) continue;

    bestDist = dist;
    best = {
      wallId: wall.id,
      start,
      end,
      point: projected,
      dist,
      t,
    };
  }

  return best;
}

function pointIsOnWallSegment(point, wallSnap, tolerance) {
  const { dist, t } = projectPointOntoSegment(wallSnap.start, wallSnap.end, point);
  return dist <= tolerance && t > 0 && t < 1;
}

function snapWallProjectionToPerpendicularIncidentWall(wallSnap, pointId, state, tolerancePx) {
  const anchors = connectedWallAnchors(state, pointId);
  if (!anchors.length) {
    return { point: wallSnap.point, snapped: false, perpendicularGuide: null };
  }

  let best = null;
  for (const anchor of anchors) {
    const { projected, t } = projectPointOntoSegment(wallSnap.start, wallSnap.end, anchor.fixed);
    if (t <= 0 || t >= 1) continue;
    const correction = Math.hypot(projected[0] - wallSnap.point[0], projected[1] - wallSnap.point[1]);
    if (correction > tolerancePx) continue;
    if (!best || correction < best.correction) {
      const targetEnd =
        Math.hypot(projected[0] - wallSnap.start[0], projected[1] - wallSnap.start[1]) <=
        Math.hypot(projected[0] - wallSnap.end[0], projected[1] - wallSnap.end[1])
          ? wallSnap.end
          : wallSnap.start;
      best = {
        point: projected,
        correction,
        perpendicularGuide: buildPerpendicularGuide(projected, anchor.fixed, targetEnd),
      };
    }
  }

  if (!best) {
    return { point: wallSnap.point, snapped: false, perpendicularGuide: null };
  }

  return {
    point: best.point,
    snapped: true,
    perpendicularGuide: best.perpendicularGuide,
  };
}

/**
 * @param {Point} pointer
 * @param {string} pointId
 * @param {DesignerState} state
 * @param {{
 *   snapToGrid?: boolean;
 *   snapToPoints?: boolean;
 *   snapToWalls?: boolean;
 *   snapTolerancePx?: number;
 *   gridMeters?: number;
 *   displayScale?: number;
 *   angleSnapEnabled?: boolean;
 *   angleSnapToleranceDeg?: number;
 *   disableSnap?: boolean;
 *   excludeWallIds?: string[];
 * }} options
 * @returns {{
 *   point: Point;
 *   angleSnap: AngleSnapMeta | null;
 *   guides?: ReturnType<typeof resolveOrthogonalAndOTrackSnap>['guides'];
 *   snapInfo?: ReturnType<typeof resolveOrthogonalAndOTrackSnap>['snapInfo'];
 * }}
 */
export function snapDraggedCorner(pointer, pointId, state, options = {}) {
  const {
    snapToGrid = true,
    snapToPoints = true,
    snapToWalls = true,
    snapTolerancePx = 12,
    gridMeters = 0.1,
    displayScale = 1,
    angleSnapEnabled: angleSnapOn = angleSnapEnabled,
    angleSnapToleranceDeg = ANGLE_SNAP_TOLERANCE_DEG,
    disableSnap = false,
    excludeWallIds = [],
  } = options;

  if (disableSnap) {
    return { point: pointer, angleSnap: null, guides: [], snapInfo: null };
  }

  const tolerance = screenToleranceToImagePx(snapTolerancePx, displayScale);
  const connectedOrtho = resolveOrthogonalAndOTrackSnap({
    activePoint: pointer,
    state,
    pointId,
    excludePointIds: [pointId],
    snapToGrid: false,
    displayScale,
    includeConnectedWallOrtho: true,
    includeOTrack: false,
  });

  const incidentWallIds = state.walls
    .filter((w) => w.startPointId === pointId || w.endPointId === pointId)
    .map((w) => w.id);
  const wallExcludeIds = [...excludeWallIds, ...incidentWallIds];
  const connectedAxisSnap = Boolean(
    connectedOrtho.snapInfo.snappedX || connectedOrtho.snapInfo.snappedY,
  );

  if (!connectedAxisSnap && angleSnapOn) {
    const perpendicularFirst = snapToPerpendicularIncidentWalls(pointer, pointId, state, {
      tolerancePx: tolerance,
      angleToleranceDeg: angleSnapToleranceDeg,
    });
    if (perpendicularFirst.snapped) {
      return {
        point: perpendicularFirst.point,
        angleSnap: perpendicularFirst.angleSnap,
        perpendicularGuide: perpendicularFirst.perpendicularGuide,
        guides: [],
        snapInfo: connectedOrtho.snapInfo,
      };
    }
  }

  if (snapToWalls && state.walls.length) {
    const wallSnap = getWallSnapCandidate(connectedOrtho.point, state, tolerance, wallExcludeIds);
    if (wallSnap) {
      let next = [...wallSnap.point];
      let guides = updateGuideEndpoints(connectedOrtho.guides, next);
      let perpendicularGuide = null;
      const snapInfo = { ...connectedOrtho.snapInfo };

      if (angleSnapOn) {
        const perpendicularOnWall = snapWallProjectionToPerpendicularIncidentWall(
          { ...wallSnap, point: next },
          pointId,
          state,
          tolerance,
        );
        if (perpendicularOnWall.snapped) {
          next = [...perpendicularOnWall.point];
          perpendicularGuide = perpendicularOnWall.perpendicularGuide;
          guides = updateGuideEndpoints(connectedOrtho.guides, next);
        }
      }

      if (snapToPoints && !perpendicularGuide) {
        const otrack = resolveOrthogonalAndOTrackSnap({
          activePoint: next,
          state,
          pointId,
          excludePointIds: [pointId],
          excludeWallIds: wallExcludeIds,
          snapToGrid: false,
          displayScale,
          includeConnectedWallOrtho: false,
          includeOTrack: true,
        });
        const candidate = [...otrack.point];
        const otrackGuides = otrack.guides.filter((guide) => {
          if (guide.type === 'vertical') return !connectedOrtho.snapInfo.snappedX;
          if (guide.type === 'horizontal') return !connectedOrtho.snapInfo.snappedY;
          return true;
        });
        if (connectedOrtho.snapInfo.snappedX) candidate[0] = next[0];
        if (connectedOrtho.snapInfo.snappedY) candidate[1] = next[1];

        if (
          otrackGuides.length &&
          pointIsOnWallSegment(candidate, wallSnap, tolerance)
        ) {
          next = candidate;
          guides = updateGuideEndpoints([...connectedOrtho.guides, ...otrackGuides], next);
          if (!connectedOrtho.snapInfo.snappedX && otrack.snapInfo.snappedX) {
            snapInfo.snappedX = true;
            snapInfo.reasonX = otrack.snapInfo.reasonX;
          }
          if (!connectedOrtho.snapInfo.snappedY && otrack.snapInfo.snappedY) {
            snapInfo.snappedY = true;
            snapInfo.reasonY = otrack.snapInfo.reasonY;
          }
        }
      }

      return { point: next, angleSnap: null, guides, snapInfo, perpendicularGuide };
    }
  }

  const orthoTrack = resolveOrthogonalAndOTrackSnap({
    activePoint: pointer,
    state,
    pointId,
    excludePointIds: [pointId],
    snapToGrid,
    gridMeters,
    displayScale,
    includeConnectedWallOrtho: true,
    includeOTrack: snapToPoints,
  });

  let next = [...orthoTrack.point];
  const consumedAxisSnap = Boolean(orthoTrack.snapInfo.snappedX || orthoTrack.snapInfo.snappedY);

  /** @type {AngleSnapMeta | null} */
  let angleSnap = null;

  if (!connectedAxisSnap && !consumedAxisSnap && angleSnapOn) {
    const perpendicularResult = snapToPerpendicularIncidentWalls(next, pointId, state, {
      tolerancePx: tolerance,
      angleToleranceDeg: angleSnapToleranceDeg,
    });
    if (perpendicularResult.snapped) {
      return {
        point: perpendicularResult.point,
        angleSnap: perpendicularResult.angleSnap,
        perpendicularGuide: perpendicularResult.perpendicularGuide,
        guides: orthoTrack.guides,
        snapInfo: orthoTrack.snapInfo,
      };
    }

    const cornerResult = constrainCornerByIncidentWalls(next, pointId, state, {
      angleSnapToleranceDeg,
    });
    next = cornerResult.point;
    if (cornerResult.snapped && cornerResult.anchor && cornerResult.snappedAngleDeg != null) {
      angleSnap = {
        snapped: true,
        snappedAngleDeg: cornerResult.snappedAngleDeg,
        anchor: cornerResult.anchor,
      };
    }
  }

  return {
    point: next,
    angleSnap,
    perpendicularGuide: null,
    guides: orthoTrack.guides,
    snapInfo: orthoTrack.snapInfo,
  };
}
